import _ from 'lodash';
import {
  lsApiService,
  lsErrorService,
  lsStorageService,
  lsStorageServiceConstants,
  lsTrackingService,
  lsToastService,
} from 'ls-vue-common';
import {
  AUTH__RESET,
  AUTH__MUTATE_TOKEN,
  AUTH__MUTATE_CUSTOMER,
  AUTH__MUTATE_USER,
  AUTH__MUTATE_LOADING_STATUS,
  AUTH__UPDATE_TOKEN,
  AUTH__GET_TOKEN_FROM_ACCESS_CODE,
  AUTH__VALIDATE_AUTHORIZED_USER,
  AUTH__LOGIN,
  AUTH__SYNC_LOGIN,
  AUTH__LOGIN_SUCCESS,
  AUTH__REQUEST_PASSWORD,
  AUTH__RESET_PASSWORD,
  AUTH__SIGN_OUT,
  AUTH__TOKEN,
  AUTH__IS_LOGGED_IN,
  AUTH__PROCESSING_ORDER,
  AUTH__JWT_FETCH,
  AUTH__JWT_IS_LOADING,
  AUTH__JWT_MUTATE,
  AUTH__JWT_RECEIVED,
  CUSTOMER_LEADS__INITIALIZE,
  ONBOARDING__INITIALIZE,
  FEATURE_FLAGS__GET_FLAGS,
  AUTH__PROCESSING_CUSTOMER_LEADS,
  ONBOARDING__INITIALIZE_LEADS,
  AUTH__IS_CUSTOMER_LEAD,
  AUTH__IS_PROPERTY_MANAGER,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
  AUTH__JWT,
} from '@/store/types';
import router from '@/router';
import { lsModuleHelpers } from '../helpers';
import { LsAuthConstants } from './auth.constants';
import { getBrandName } from '@/helpers';

const PROPERTY_MANAGER_TYPE_CONSTANT = 'propertyManager';

const createInitialState = () => {
  return {
    token: lsStorageService.get(lsStorageServiceConstants.AUTH_TOKEN),
    user: {
      customer: {},
    },
    loadingStatus: {
      isInitializing: false,
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [AUTH__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [AUTH__MUTATE_TOKEN](state, payload) {
    state.token = payload;
  },

  [AUTH__MUTATE_USER](state, payload) {
    state.user = {
      ...state.user,
      ...payload,
    };
  },

  [AUTH__MUTATE_CUSTOMER](state, payload) {
    state.user.customer = {
      ...state.user.customer,
      ...payload,
    };
  },

  [AUTH__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },

  [AUTH__JWT_IS_LOADING](state, { isLoading }) {
    state.isLoading = isLoading;
  },

  [AUTH__JWT_MUTATE](state, { jwt }) {
    state.jwt = `Bearer ${jwt}`;
  },
};

export const actions = {
  [AUTH__UPDATE_TOKEN]({ commit }, token) {
    commit(AUTH__MUTATE_TOKEN, token);

    if (token) {
      lsStorageService.set(lsStorageServiceConstants.AUTH_TOKEN, token);
    } else {
      lsStorageService.remove(lsStorageServiceConstants.AUTH_TOKEN);
    }
  },

  [AUTH__GET_TOKEN_FROM_ACCESS_CODE]({ commit, dispatch }, { access, onSuccess }) {
    commit(AUTH__MUTATE_LOADING_STATUS, {
      isInitializing: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: '/auth/access-code',
        payload: { access },
      })
      .then(({ data }) => {
        dispatch(AUTH__UPDATE_TOKEN, data.authtoken);
        dispatch(AUTH__VALIDATE_AUTHORIZED_USER, { user: data.user, onSuccess });
      })
      .catch((error) => {
        lsErrorService.log({ error });
        if (error.suppressAlert) {
          // dont signout if its maintenanceMode, update required or offline error
          return;
        }
        dispatch(AUTH__SIGN_OUT);
      });
  },

  [AUTH__VALIDATE_AUTHORIZED_USER]({ getters, commit, dispatch }, { user, onSuccess }) {
    // if user is NOT a customer
    if (!_.get(user, 'customer_id')) {
      //  call signout
      lsToastService.open({
        message: `This email address is not associated with a ${getBrandName()} customer account`,
        type: 'is-error',
      });

      return dispatch(AUTH__SIGN_OUT);
    }

    if (user.customer.type !== PROPERTY_MANAGER_TYPE_CONSTANT) {
      // if customer is NOT a PM call login success
      return dispatch(AUTH__LOGIN_SUCCESS, { user, onSuccess });
    }

    //  otherwise they are a PM, so send to propertymanager.
    const token = getters[AUTH__TOKEN];

    lsStorageService.clear(_.cloneDeep(_.values(lsStorageServiceConstants)));
    commit(AUTH__MUTATE_TOKEN, null);

    window.location.assign(`${process.env.VUE_APP_PROPERY_MANAGER_BASE_URL}?auth=${token}`);
  },

  // called when the app is initialized if an authtoken exists
  // OR after a user signs in because we need to populate user off the /me call for referral information
  [AUTH__LOGIN]({ commit, dispatch }, { onSuccess } = {}) {
    commit(AUTH__MUTATE_LOADING_STATUS, {
      isInitializing: true,
    });

    return lsApiService
      .request({
        route: '/me',
      })
      .then(({ data }) => {
        dispatch(AUTH__JWT_FETCH);
        dispatch(AUTH__VALIDATE_AUTHORIZED_USER, { user: data, onSuccess });
      })
      .catch((error) => {
        lsErrorService.log({ error });
        if (error.suppressAlert) {
          // dont signout if its maintenanceMode, update required or offline error
          return;
        }
        dispatch(AUTH__SIGN_OUT);
      })
      .finally(() => {
        commit(AUTH__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  [AUTH__JWT_FETCH]({ commit, dispatch }) {
    commit(AUTH__JWT_IS_LOADING, { isLoading: true });
    return lsApiService
      .request({ route: '/auth/partnersapi/token', method: 'POST' })
      .then(({ token }) => dispatch(AUTH__JWT_RECEIVED, token))
      .catch((error) => lsErrorService.log({ error }))
      .finally(() => commit(AUTH__JWT_IS_LOADING, { isLoading: false }));
  },

  [AUTH__JWT_RECEIVED]({ commit }, token) {
    commit(AUTH__JWT_MUTATE, { jwt: token });
    lsStorageService.set(AUTH__JWT, token);
  },

  // called from the signin screen, on success, we set token and validate the user.
  [AUTH__SYNC_LOGIN]({ commit, dispatch }, { email, password }) {
    commit(AUTH__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: '/auth',
        payload: { email, password },
      })
      .then(({ data }) => {
        dispatch(AUTH__UPDATE_TOKEN, data.authtoken);
        dispatch(AUTH__LOGIN);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
        commit(AUTH__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  // called after AUTH__VALIDATE_AUTHORIZED_USER validates the user is a customer
  async [AUTH__LOGIN_SUCCESS]({ commit, getters, dispatch }, { user, onSuccess }) {
    commit(AUTH__MUTATE_USER, user);
    const { user_id, email, first_name, last_name, hash_id } = user;

    lsErrorService.setUserContext({ user_id, email });
    lsTrackingService.identify({ user_id: hash_id, email, name: `${first_name} ${last_name}` });

    if (getters[AUTH__IS_CUSTOMER_LEAD]) {
      dispatch(CUSTOMER_LEADS__INITIALIZE);
    }
    // if processing_order is non-null, the customer is onboarding
    if (getters[AUTH__PROCESSING_ORDER] || getters[AUTH__PROCESSING_CUSTOMER_LEADS]) {
      await dispatch(FEATURE_FLAGS__GET_FLAGS);
      getters[AUTH__PROCESSING_ORDER]
        ? dispatch(ONBOARDING__INITIALIZE)
        : dispatch(ONBOARDING__INITIALIZE_LEADS);
    } else if (onSuccess) {
      // need to await flags call to determine if customers.propertyManagers.propertyList flag is enabled
      await dispatch(FEATURE_FLAGS__GET_FLAGS);

      // otherwise check if an onSuccess cb exists, if so, the user trying to directly reach
      // an authenticated route and here we'll let them proceed to it

      onSuccess();
    } else {
      // need to await flags call to determine if customers.propertyManagers.propertyList flag is enabled
      await dispatch(FEATURE_FLAGS__GET_FLAGS);

      const isPropertyManager = getters[AUTH__IS_PROPERTY_MANAGER];
      dispatch(FEATURE_FLAGS__REGISTER_EXPOSURE, {
        flag: 'customers.propertyManagers.propertyList',
      });

      // otherwise, the user is logging in fresh - so we send them to services
      // or properties if they are a property manager
      router.replace({
        name: isPropertyManager ? 'properties' : 'services',
      });
    }

    commit(AUTH__MUTATE_LOADING_STATUS, {
      isInitializing: false,
      isLoading: false,
    });
  },

  [AUTH__REQUEST_PASSWORD]({ commit }, { email, onSuccess = _.noop }) {
    commit(AUTH__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: '/auth/recover',
        payload: { email },
      })
      .then(() => {
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(AUTH__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [AUTH__RESET_PASSWORD]({ commit }, { token, password, password_repeat, onSuccess = _.noop }) {
    commit(AUTH__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/auth/reset`,
        payload: { token, password, password_repeat },
      })
      .then(() => {
        lsToastService.open({
          message: 'Your password has been changed. You can now login with your new password.',
          type: 'is-success',
          position: 'is-top',
          duration: 5000,
        });
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(AUTH__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [AUTH__SIGN_OUT]({ commit }, { routeName = 'login', routeParams = {} } = {}) {
    lsStorageService.remove(AUTH__JWT);
    commit(AUTH__JWT_MUTATE, { jwt: null });
    // Clear out storage of tokens, and token in state to allow login replace immediately prior to resetting all other modules
    lsStorageService.clear(_.cloneDeep(_.values(lsStorageServiceConstants)));
    commit(AUTH__MUTATE_TOKEN, null);
    router.replace({ name: routeName, params: routeParams });

    _.forEach(LsAuthConstants.mutationsToRunOnSignOut, (COMMIT_NAME) => {
      commit(COMMIT_NAME);
    });
  },
};

export const getters = {
  [AUTH__TOKEN](state) {
    return _.get(state, 'token');
  },
  [AUTH__IS_LOGGED_IN](state) {
    return !_.isEmpty(state.user.customer);
  },
  [AUTH__PROCESSING_ORDER](state) {
    return _.get(state.user.customer, 'processing_order');
  },
  [AUTH__PROCESSING_CUSTOMER_LEADS](state) {
    return _.get(state.user.customer, 'processing_customer_leads[0]') || null;
  },
  [AUTH__IS_CUSTOMER_LEAD](state) {
    return state.user.customer.is_customer_lead;
  },
  [AUTH__IS_PROPERTY_MANAGER](state, __, rootState) {
    return (
      rootState.featureFlags.flags['customers.propertyManagers.propertyList'] &&
      _.get(state.user.customer, 'property_count') >= 10
    );
  },
};

export const auth = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
